import { useEffect, useRef, useState, type ChangeEvent } from "react";

import { Field, input } from "@/components/form";
import * as products from "@/lib/api/products";
import { ApiError } from "@/lib/api-client";
import type { AdminImage, AdminProductDetail, ProductColour } from "@/lib/api/types";
import { cn } from "@/lib/utils";

/**
 * The three editors that own a product's *presentation* — its photographs, its
 * finishes and its feature tags (ADMIN.md §9, phase 5c).
 *
 * All three follow the same shape: they hold no copy of the product, they call
 * the API, and they hand the fresh detail response back to the parent. The
 * endpoints all return the complete product for exactly this reason — a screen
 * that patched its own local state after a write would drift from the server
 * the first time the server did something the client did not predict, which
 * here is routine: uploading an image renumbers positions, and deleting one
 * closes the gap behind it.
 */

/** Refused in the browser rather than after a slow upload. Matches UPLOAD_MAX_BYTES. */
const MAX_BYTES = 10 * 1024 * 1024;

const ACCEPT = "image/jpeg,image/png,image/webp,image/avif";

const button =
  "rounded-lg border border-line px-2.5 py-1 text-[12px] font-medium text-body hover:bg-canvas disabled:opacity-50";

// ─── Images ──────────────────────────────────────────────────────────────────

export function ImageManager({
  product,
  onApplied,
}: {
  product: AdminProductDetail;
  onApplied: (fresh: AdminProductDetail) => void;
}) {
  const images = product.images ?? [];

  const [alt, setAlt] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  async function run(label: string, action: () => Promise<AdminProductDetail>) {
    setBusy(label);
    setError(null);

    try {
      onApplied(await action());
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : "That did not work.");
    } finally {
      setBusy(null);
    }
  }

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    /**
     * Reset the picker immediately, not after the upload. Without it, picking
     * the same file twice in a row fires no `change` event at all — the value
     * has not changed — and the second attempt looks like a dead button.
     */
    event.target.value = "";

    if (!file) return;

    if (!alt.trim()) {
      setError("Describe the photograph first — alt text is required.");
      return;
    }

    if (file.size > MAX_BYTES) {
      setError(`That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is 10 MB.`);
      return;
    }

    await run("upload", () => products.addImage(product.slug, file, alt.trim()));
    setAlt("");
  }

  /** Moves one image by one place, which is a reorder of the whole gallery. */
  function move(id: string, direction: -1 | 1) {
    const order = images.map((image) => image.id);
    const from = order.indexOf(id);
    const to = from + direction;

    if (from < 0 || to < 0 || to >= order.length) return;

    const [moved] = order.splice(from, 1);
    if (moved === undefined) return;

    order.splice(to, 0, moved);

    void run("order", () => products.reorderImages(product.slug, order));
  }

  return (
    <section className="mb-4 rounded-lg border border-line bg-surface p-3">
      <h3 className="mb-2 text-[11px] font-bold tracking-wide text-faint uppercase">
        Images{images.length > 0 ? ` (${images.length})` : ""}
      </h3>

      {images.length === 0 ? (
        <p className="mb-3 text-[12px] text-bad">
          No photographs yet. This product cannot be made active until it has at least one.
        </p>
      ) : (
        <ul className="mb-3 grid gap-2">
          {images.map((image, index) => (
            <ImageRow
              key={image.id}
              image={image}
              index={index}
              last={index === images.length - 1}
              only={images.length === 1}
              busy={busy !== null}
              onMove={move}
              onAlt={(text) =>
                void run("alt", () => products.updateImage(product.slug, image.id, text))
              }
              onDelete={() =>
                void run("delete", () => products.deleteImage(product.slug, image.id))
              }
            />
          ))}
        </ul>
      )}

      <Field
        label="Alt text for the next upload"
        hint="What the photograph shows. Required — a screen reader and an image search have nothing else to go on."
      >
        <input
          type="text"
          value={alt}
          onChange={(event) => setAlt(event.target.value)}
          placeholder="The earbuds, case open, three-quarter view"
          className={input}
        />
      </Field>

      <div className="mt-2 flex items-center gap-2">
        <input
          ref={picker}
          type="file"
          accept={ACCEPT}
          onChange={(event) => void upload(event)}
          className="hidden"
        />
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => picker.current?.click()}
          className={button}
        >
          {busy === "upload" ? "Uploading…" : "Add image"}
        </button>
        <span className="text-[11px] text-faint">
          JPEG, PNG, WebP or AVIF up to 10 MB. Stored as WebP, at most 1600px.
        </span>
      </div>

      {error ? <p className="mt-2 text-[12px] text-bad">{error}</p> : null}
    </section>
  );
}

function ImageRow({
  image,
  index,
  last,
  only,
  busy,
  onMove,
  onAlt,
  onDelete,
}: {
  image: AdminImage;
  index: number;
  last: boolean;
  /** The product's last remaining photograph, which cannot be removed. */
  only: boolean;
  busy: boolean;
  onMove: (id: string, direction: -1 | 1) => void;
  onAlt: (alt: string) => void;
  onDelete: () => void;
}) {
  const [alt, setAlt] = useState(image.alt);
  const [confirming, setConfirming] = useState(false);

  /**
   * Resynchronise when the server's value changes, without an effect.
   *
   * The parent replaces this row's props after every write, and a stale local
   * draft would silently undo somebody else's alt-text edit on the next save.
   * Comparing against the last prop *during render* is React's own answer to
   * that (react.dev, "You Might Not Need an Effect") — an effect would render
   * the old value once before correcting it.
   */
  const [lastSeen, setLastSeen] = useState(image.alt);

  if (lastSeen !== image.alt) {
    setLastSeen(image.alt);
    setAlt(image.alt);
  }

  return (
    <li className="flex gap-3 rounded-lg border border-line p-2">
      <img
        src={image.url}
        alt={image.alt}
        className="h-20 w-20 shrink-0 rounded-md bg-canvas object-cover"
        loading="lazy"
      />

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide uppercase",
              index === 0 ? "bg-ink text-white" : "bg-canvas text-faint",
            )}
          >
            {index === 0 ? "Cover" : `#${index + 1}`}
          </span>
          {image.width && image.height ? (
            <span className="text-[11px] text-faint">
              {image.width}×{image.height}
            </span>
          ) : null}
        </div>

        <input
          type="text"
          value={alt}
          onChange={(event) => setAlt(event.target.value)}
          onBlur={() => {
            if (alt.trim() && alt !== image.alt) onAlt(alt.trim());
          }}
          className={input}
        />

        <div className="mt-1.5 flex gap-1.5">
          <button
            type="button"
            disabled={busy || index === 0}
            onClick={() => onMove(image.id, -1)}
            className={button}
          >
            ↑
          </button>
          <button
            type="button"
            disabled={busy || last}
            onClick={() => onMove(image.id, 1)}
            className={button}
          >
            ↓
          </button>

          {/**
           * The last photograph has no Remove button at all, rather than one
           * that errors when pressed. The API refuses it either way — that is
           * the actual rule — but a control that exists only to reject you is
           * worse than a sentence explaining the order to do things in.
           */}
          {only ? (
            <span className="self-center text-[11px] text-faint">
              The only image — upload another before removing this one.
            </span>
          ) : confirming ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={onDelete}
                className="rounded-lg border border-bad px-2.5 py-1 text-[12px] font-medium text-bad disabled:opacity-50"
              >
                Really remove
              </button>
              <button type="button" onClick={() => setConfirming(false)} className={button}>
                Keep
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setConfirming(true)} className={button}>
              Remove
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

// ─── Finishes ────────────────────────────────────────────────────────────────

/**
 * The swatch row, edited as a whole.
 *
 * Local state until Save, unlike the image list above, and the difference is
 * not arbitrary: the API replaces the entire set in one call, so saving on
 * every keystroke would mean a half-typed colour name briefly *is* the
 * product's finish list.
 */
export function ColourEditor({
  colours,
  onSave,
  onChange,
}: {
  colours: ProductColour[];
  /** On an existing product: writes the whole set through the API on Save. */
  onSave?: (next: ProductColour[]) => Promise<void>;
  /**
   * On the create form: there is no product to write to yet, so every edit is
   * handed straight up and no Save button is drawn. Exactly one of the two is
   * passed.
   */
  onChange?: (next: ProductColour[]) => void;
}) {
  const [draft, setDraft] = useState<ProductColour[]>(colours);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** See the note in `ImageRow`. */
  const [lastSeen, setLastSeen] = useState(colours);

  if (lastSeen !== colours) {
    setLastSeen(colours);
    setDraft(colours);
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(colours);

  function apply(next: ProductColour[]) {
    setDraft(next);
    onChange?.(next);
  }

  function edit(index: number, patch: Partial<ProductColour>) {
    apply(draft.map((entry, at) => (at === index ? { ...entry, ...patch } : entry)));
  }

  async function save() {
    setBusy(true);
    setError(null);

    try {
      await onSave?.(
        draft.map((entry) => ({ name: entry.name.trim(), swatch: entry.swatch.trim() })),
      );
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : "Those finishes were not saved.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-4 rounded-lg border border-line bg-surface p-3">
      <h3 className="mb-2 text-[11px] font-bold tracking-wide text-faint uppercase">Finishes</h3>

      {draft.length === 0 ? (
        <p className="text-[12px] text-faint">
          None. The PDP simply draws no swatch row, which is correct for a product sold in one
          colour.
        </p>
      ) : (
        <ul className="grid gap-2">
          {draft.map((colour, index) => (
            <li key={index} className="flex items-center gap-2">
              <span
                className="h-7 w-7 shrink-0 rounded-full border border-line"
                style={{ background: colour.swatch }}
                aria-hidden
              />
              <input
                type="text"
                value={colour.name}
                onChange={(event) => edit(index, { name: event.target.value })}
                placeholder="Jet Black"
                className={input}
              />
              <input
                type="text"
                value={colour.swatch}
                onChange={(event) => edit(index, { swatch: event.target.value })}
                placeholder="#111111"
                className={cn(input, "max-w-[9rem] font-mono")}
              />
              <button
                type="button"
                onClick={() => apply(draft.filter((_, at) => at !== index))}
                className={button}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => apply([...draft, { name: "", swatch: "#000000" }])}
          className={button}
        >
          Add finish
        </button>

        {onSave ? (
          <button
            type="button"
            disabled={!dirty || busy || draft.some((entry) => !entry.name.trim())}
            onClick={() => void save()}
            className="rounded-lg bg-ink px-2.5 py-1 text-[12px] font-semibold text-white disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save finishes"}
          </button>
        ) : null}

        <span className="text-[11px] text-faint">
          Any CSS colour — a hex, a name, or an `oklch()`. It is rendered as a swatch, never as
          markup.
        </span>
      </div>

      {error ? <p className="mt-2 text-[12px] text-bad">{error}</p> : null}
    </section>
  );
}

// ─── Feature tags ────────────────────────────────────────────────────────────

/**
 * The filter rail, as a set of toggles.
 *
 * A picker rather than a text field, because the API refuses any label outside
 * the `Feature` table — deliberately, since a typo would otherwise become a
 * permanent storefront filter matching exactly one product. Offering only what
 * exists means the operator never meets that refusal.
 */
export function FeaturePicker({
  selected,
  onSave,
  onChange,
}: {
  selected: string[];
  /** On an existing product: writes the whole set through the API on Save. */
  onSave?: (next: string[]) => Promise<void>;
  /** On the create form: edits stay local until the product exists. */
  onChange?: (next: string[]) => void;
}) {
  const [vocabulary, setVocabulary] = useState<string[] | null>(null);
  const [draft, setDraft] = useState<string[]>(selected);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** See the note in `ImageRow`. */
  const [lastSeen, setLastSeen] = useState(selected);

  if (lastSeen !== selected) {
    setLastSeen(selected);
    setDraft(selected);
  }

  useEffect(() => {
    const controller = new AbortController();

    void products
      .fetchFeatures(controller.signal)
      .then(setVocabulary)
      .catch(() => {
        if (!controller.signal.aborted) setVocabulary([]);
      });

    return () => controller.abort();
  }, []);

  const dirty = JSON.stringify([...draft].sort()) !== JSON.stringify([...selected].sort());

  async function save() {
    setBusy(true);
    setError(null);

    try {
      await onSave?.(draft);
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : "Those tags were not saved.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-4 rounded-lg border border-line bg-surface p-3">
      <h3 className="mb-2 text-[11px] font-bold tracking-wide text-faint uppercase">
        Feature tags
      </h3>

      {vocabulary === null ? (
        <p className="text-[12px] text-faint">Loading the tag list…</p>
      ) : vocabulary.length === 0 ? (
        <p className="text-[12px] text-faint">No tags are defined.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {vocabulary.map((label) => {
            const on = draft.includes(label);

            return (
              <button
                key={label}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  const next = on ? draft.filter((entry) => entry !== label) : [...draft, label];

                  setDraft(next);
                  onChange?.(next);
                }}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[12px]",
                  on
                    ? "border-ink bg-ink font-medium text-white"
                    : "border-line text-body hover:bg-canvas",
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {onSave ? (
        <button
          type="button"
          disabled={!dirty || busy}
          onClick={() => void save()}
          className="mt-2 rounded-lg bg-ink px-2.5 py-1 text-[12px] font-semibold text-white disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save tags"}
        </button>
      ) : null}

      {error ? <p className="mt-2 text-[12px] text-bad">{error}</p> : null}
    </section>
  );
}
